/*
 * @Author: Christion.Young
 * @Date: 2021-07-20 09:46:04
 * @LastEditors: Christion.Young
 * @LastEditTime: 2025-08-14 14:00:00
 * @Description: HTTP请求工具 - Axios请求拦截器
 */
import axios from "axios";
import loading from "@/components/Common/Loading/index";
import { Message } from "element-ui";
import store from "@/store/index.js";
import router from "@/router";

// 创建axios实例
const service = axios.create({
  baseURL: "/api/",
  timeout: 50000,
  // 添加CORS配置
  withCredentials: false, // 如果不需要发送cookies，设置为false
  crossDomain: true,
});

// Loading实例
let loadingVm = loading();

// 是否正在刷新token
let isRefreshing = false;
// 失败请求队列
let failedQueue = [];

/**
 * 处理失败队列
 * @param {Error} error - 错误对象
 * @param {string} token - 新token
 */
function processQueue(error, token = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
}

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    // 显示loading
    config.loading ? loadingVm.handlemount() : "";

    // 设置请求头
    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json;charset=UTF-8";
    }
    config.headers["Accept"] = "application/json";

    // 跳过token的请求（如OAuth2登录）
    if (config.headers.skipToken) {
      delete config.headers.skipToken;
      return config;
    }

    // 添加token
    const token = store.getters["user/token"];
    if (token) {
      config.headers["token"] = token;
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    loadingVm.handleDestory();
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  (response) => {
    loadingVm.handleDestory();

    // 统一处理响应
    const { code, message } = response.data;

    // 成功响应
    if (response.data.success === true || code === 0) {
      return response;
    }

    // Token相关错误处理
    if (code === 401 || code === 403) {
      handleTokenError(response.data);
      return Promise.reject(new Error(message || "Token验证失败"));
    }

    // 其他业务错误
    if (message) {
      Message.error(message);
    }

    return response;
  },
  async (error) => {
    loadingVm.handleDestory();

    const { config, response } = error;

    // 处理401错误（token过期）
    if (response && response.status === 401) {
      return handleUnauthorized(config);
    }

    // 处理403错误（权限不足）
    if (response && response.status === 403) {
      Message.error("权限不足，请联系管理员");
      return Promise.reject(error);
    }

    // 处理500错误
    if (response && response.status >= 500) {
      Message.error("服务器内部错误，请稍后重试");
      return Promise.reject(error);
    }

    // 网络错误
    if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
      Message.error("请求超时，请检查网络连接");
    } else if (!response) {
      Message.error("网络连接失败，请检查网络设置");
    } else {
      Message.error(error.message || "请求失败");
    }

    return Promise.reject(error);
  }
);

/**
 * 处理Token错误
 * @param {Object} responseData - 响应数据
 */
function handleTokenError(responseData) {
  const { message } = responseData;

  console.warn("Token验证失败:", message);

  // 清除用户状态
  store.dispatch("user/logOut");

  // 跳转到首页
  if (router.currentRoute.path !== "/") {
    router.push("/");
  }

  // 显示错误信息
  Message.error(message || "Token验证失败，请重新登录");
}

/**
 * 处理401未授权错误
 * @param {Object} config - 请求配置
 * @returns {Promise} 处理结果
 */
async function handleUnauthorized(config) {
  // 如果正在刷新token，将请求加入队列
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then(() => {
        return service(config);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  // 开始刷新token
  isRefreshing = true;

  try {
    // 尝试刷新token
    const refreshToken = store.getters["user/refreshToken"];
    if (!refreshToken) {
      throw new Error("无可用的刷新令牌");
    }

    const newTokenData = await store.dispatch(
      "user/refreshToken",
      refreshToken
    );
    const newToken = newTokenData.access_token;

    // 更新请求头
    config.headers["token"] = newToken;
    config.headers["Authorization"] = `Bearer ${newToken}`;

    // 处理队列中的请求
    processQueue(null, newToken);

    // 重新发送原请求
    return service(config);
  } catch (error) {
    console.error("Token刷新失败:", error);

    // 处理队列中的请求
    processQueue(error, null);

    // 清除用户状态并跳转
    store.dispatch("user/logOut");

    // 显示登录弹窗
    setTimeout(() => {
      if (router.app && router.app.$bus) {
        router.app.$bus.emit("show-login-dialog", {
          redirectPath: router.currentRoute.fullPath,
        });
      }
    }, 100);

    return Promise.reject(error);
  } finally {
    isRefreshing = false;
  }
}

/**
 * @description: GET请求
 * @param {String} url 请求地址
 * @param {Object} params 参数
 * @param {Boolean} loading 开关
 * @return {*}
 */
export function get(url, params = {}, loading = true) {
  return new Promise((resolve, reject) => {
    service({
      url: url,
      method: "get",
      params: params,
      loading: loading,
    })
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * @description: POST请求
 * @param {String} url 请求地址
 * @param {Object} params 参数
 * @param {Boolean} loading 开关
 * @return {*}
 */
export function post(url, params = {}, loading = true) {
  return new Promise((resolve, reject) => {
    service({
      url: url,
      method: "post",
      data: params,
      loading: loading,
    })
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * @description: PUT请求
 * @param {String} url 请求地址
 * @param {Object} params 参数
 * @param {Boolean} loading 开关
 * @return {*}
 */
export function put(url, params = {}, loading = true) {
  return new Promise((resolve, reject) => {
    service({
      url: url,
      method: "put",
      data: params,
      loading: loading,
    })
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * @description: DELETE请求
 * @param {String} url 请求地址
 * @param {Object} params 参数
 * @param {Boolean} loading 开关
 * @return {*}
 */
export function delet(url, params = {}, loading = false) {
  return new Promise((resolve, reject) => {
    service({
      url: url,
      method: "delete",
      params: params || {},
      loading: loading,
    })
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * @description: 文件上传
 * @param {String} url 请求地址
 * @param {Object} params 参数
 * @param {Boolean} loading 开关
 * @return {*}
 */
export function fileUpload(url, params = {}, loading = true) {
  return new Promise((resolve, reject) => {
    service({
      url: url,
      method: "post",
      data: params,
      loading: loading,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * @description: 文件下载
 * @param {String} url 请求地址
 * @param {Object} params 参数
 * @param {Boolean} loading 开关
 * @return {*}
 */
export function exportFile(url, params, loading = true) {
  return new Promise((resolve, reject) => {
    service({
      url: url,
      method: "get",
      params,
      loading: loading,
      responseType: "blob",
    })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

export default {
  get,
  post,
  put,
  delet,
  fileUpload,
  exportFile,
};
